import React, { Component } from 'react';
import propTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import isEqual from 'lodash/isEqual';

// components
import PrescriptionsFlags from 'manage/PrescriptionsFlags';
import OptionsMenu from 'manage/OptionsMenu';
import FertilizerRateThumbnail from 'dataviz/FertilizerRateThumbnail';
import Name from 'manage/Name';
import Timestamp from 'manage/Timestamp';
import FertilizerSummary from 'manage/FertilizerSummary';

// Selectors
import { getActivityCategoriesByActivityType } from 'activities/selectors';
import { getActiveScriptId, scriptOptionsIsOpenForScriptId } from 'current/selectors';
import { canManualScript } from 'product-features/selectors';
import {
  getActivityTypeForScript,
  isAdvancedScript,
  isDisplayPrimaryScript
} from 'scripts/selectors';
import {
  getManageFertilizerSummary
} from 'scripts/fertility-selectors';
import {
  getProductValidationValues
} from 'user/selectors';

// Actions
import { setScriptOptionsIsOpen } from 'current/uiwidgets/';
import { editScript } from 'scripts';
import { requestDriveDevices, requestPreferredDevice } from 'devices/requesters';
import { requestAffiliateList } from 'affiliates/requesters';

const baseString = 'vra-prescription-card';
import { trackAction } from 'util/analytics';

class FertilityScriptCardContainer extends Component {
  shouldComponentUpdate(nextProps, nextState) {
    return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
  }

  render() {
    const {
      activity,
      canManualScript,
      fertilizerSummary,
      isSelected,
      onClickEditTrigger,
      onClickScriptOptionsTrigger,
      onSelectScript,
      productValidationValues,
      script,
      scriptId,
      scriptOptionsIsOpen,
      showPrimaryFlag,
      onHideCallbackOptionsTrigger
    } = this.props;

    const classString = classNames({
      [`${baseString}`]: true,
      'isSelected': isSelected,
      [`${baseString}--manual-fertility`]: true
    });
    const dataTestId = showPrimaryFlag ? 'script-primary' : 'script-not-primary';

    return (
      <div
        className={classString}
        data-test-id={baseString}
        key={scriptId}
        onClick={() => {
          trackAction('Clicked on a fertilty script on the left panel', { scriptId });
          onSelectScript(scriptId);
        }}
      >
        <PrescriptionsFlags
          createdAt={script['create-time']}
          isPrimary={showPrimaryFlag}
          program={script.attributes?.program}
        />
        <div className={`${baseString}__identification-section`}>
          <FertilizerRateThumbnail productValidationValues={productValidationValues} script={script}/>
          <div className={`${baseString}__name-section`}>
            <Name
              canManualScript={canManualScript}
              isSelected={isSelected}
              name={script.name}
              onClickEditTrigger={onClickEditTrigger}
              scriptId={scriptId}
              testId="script-fertility-name"
            />
            <Timestamp timestamp={script['update-time']}/>
          </div>
          <OptionsMenu
            onClick={() => onClickScriptOptionsTrigger({scriptId, currentStatus: scriptOptionsIsOpen})}
            onHideCallback={() => onHideCallbackOptionsTrigger({ scriptId })}
            scriptOptionsIsOpen={scriptOptionsIsOpen}
          />
        </div>
        <section className="vra-prescription-card__content-wrapper" data-test-id={dataTestId}>
          <FertilizerSummary activity={activity} summary={fertilizerSummary}/>
        </section>
      </div>
    );
  }
}

FertilityScriptCardContainer.propTypes = {
  activity: propTypes.object.isRequired,
  activityType: propTypes.string,
  canManualScript: propTypes.bool.isRequired,
  fertilizerSummary: propTypes.arrayOf(propTypes.object),
  isAdvancedScript: propTypes.bool,
  isSelected: propTypes.bool,
  onClickEditTrigger: propTypes.func,
  onClickScriptOptionsTrigger: propTypes.func,
  onHideCallbackOptionsTrigger: propTypes.func,
  onSelectScript: propTypes.func,
  productValidationValues: propTypes.object,
  script: propTypes.object,
  scriptId: propTypes.string,
  scriptOptionsIsOpen: propTypes.bool,
  showPrimaryFlag: propTypes.bool
};

FertilityScriptCardContainer = connect(
  // mapStateToProps
  (state, ownProps) => {
    const script = ownProps.script;
    const scriptId = script.id;
    const activityCategories = getActivityCategoriesByActivityType(state);
    const activityType = getActivityTypeForScript(script);
    return {
      activity: activityCategories[activityType],
      canManualScript: canManualScript(state),
      fertilizerSummary: getManageFertilizerSummary(script),
      isAdvancedScript: isAdvancedScript(script),
      isSelected: getActiveScriptId(state) === scriptId,
      onSelectScript: ownProps.onSelectScript,
      productValidationValues: getProductValidationValues(state),
      script: script,
      scriptId: scriptId,
      scriptOptionsIsOpen: scriptOptionsIsOpenForScriptId(state, scriptId),
      showPrimaryFlag: isDisplayPrimaryScript(state, script)
    };
  },
  (dispatch, ownProps) => {
    return {
      onClickEditTrigger: ({ scriptId, uiLocation }) => {
        dispatch(editScript({ scriptId, uiLocation }));
      },
      onClickScriptOptionsTrigger: ({ scriptId, currentStatus }) => {
        ownProps.onSelectScript(scriptId);
        dispatch(requestPreferredDevice());
        dispatch(requestDriveDevices());
        dispatch(requestAffiliateList());
        dispatch(setScriptOptionsIsOpen({ scriptId, status: !currentStatus }));
      },
      onHideCallbackOptionsTrigger: ({ scriptId }) => {
        dispatch(setScriptOptionsIsOpen({ scriptId, status: false }));
      }
    };
  }
)(FertilityScriptCardContainer);

export default FertilityScriptCardContainer;
